import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const GOLD = '#B8860B';

function getTimeLeft(deadline) {
  const diff = new Date(deadline) - new Date();
  if (diff <= 0) return { expired: true, hours: 0, minutes: 0, seconds: 0 };
  return {
    expired: false,
    hours: Math.floor(diff / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000)
  };
}

function formatShort(tl) {
  if (tl.expired) return null;
  if (tl.hours >= 24) {
    const days = Math.floor(tl.hours / 24);
    const hrs = tl.hours % 24;
    return `${days}d ${hrs}h`;
  }
  if (tl.hours > 0) return `${tl.hours}h ${tl.minutes}m`;
  return `${tl.minutes}m`;
}

function formatDetailed(tl) {
  if (tl.expired) return '0:00';
  if (tl.hours > 0) return `${tl.hours}:${String(tl.minutes).padStart(2, '0')}:${String(tl.seconds).padStart(2, '0')}`;
  return `${tl.minutes}:${String(tl.seconds).padStart(2, '0')}`;
}

export function getTimeRemainingShort(deadline) {
  const diff = new Date(deadline) - new Date();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes} min`;
}

export function ProjectStatusBlock({ status, deadline }) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(deadline));

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getTimeLeft(deadline)), 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  const isActive = status === 'in_production' || status === 'received';
  const timeStr = formatShort(timeLeft);

  if (status === 'ready') {
    return (
      <div data-testid="project-status-block" className="glass-pill px-4 py-2 rounded-full">
        <span className="text-xs font-medium text-emerald-600">Ready</span>
      </div>
    );
  }

  if (status === 'delivered') {
    return (
      <div data-testid="project-status-block" className="glass-pill px-4 py-2 rounded-full">
        <span className="text-xs font-medium text-emerald-600">Delivered</span>
      </div>
    );
  }

  if (status === 'revision_requested') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="project-status-block" className="glass-pill px-4 py-2 rounded-full cursor-pointer animate-breathe">
            {timeStr ? (
              <span className="text-xs font-semibold font-mono animate-breathe-text text-amber-600">{timeStr}</span>
            ) : (
              <span className="text-xs font-medium text-amber-600">In Revision</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-4 glass-pill rounded-2xl border-none" align="end" sideOffset={8}>
          <p className="text-[13px] text-gray-800 leading-relaxed mb-1.5">
            Revision in progress.
          </p>
          <p className="text-[11px] text-gray-400">
            Our team is reviewing your feedback.
          </p>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="project-status-block"
          className={`glass-pill px-4 py-2 rounded-full cursor-pointer transition-all ${isActive ? 'animate-breathe' : ''}`}
        >
          {isActive && timeStr ? (
            <span className="text-xs font-semibold font-mono animate-breathe-text" style={{ color: GOLD }}>{timeStr}</span>
          ) : (
            <span className="text-xs font-medium text-gray-400">--</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-4 glass-pill rounded-2xl border-none" align="end" sideOffset={8}>
        <p className="text-[13px] text-gray-800 leading-relaxed mb-1.5">
          Your video is currently in production.
        </p>
        <p className="text-[13px] text-gray-500 leading-relaxed">
          You will receive it before this time.
        </p>
        {isActive && !timeLeft.expired && (
          <p className="text-xs mt-3 font-mono font-semibold" style={{ color: GOLD }}>
            {formatDetailed(timeLeft)}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function ChatTimer({ deadline }) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(deadline));
  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getTimeLeft(deadline)), 60000);
    return () => clearInterval(interval);
  }, [deadline]);
  const timeStr = formatShort(timeLeft);
  if (!timeStr) return null;
  return (
    <div data-testid="chat-inline-timer" className="flex justify-center py-3">
      <span className="text-[11px] font-mono animate-breathe-text" style={{ color: GOLD }}>{timeStr}</span>
    </div>
  );
}

export function CountdownTimer({ deadline }) {
  return <ProjectStatusBlock status="in_production" deadline={deadline} />;
}
